import React, { useState, useEffect, useRef } from 'react';

interface ImageWithLoaderProps {
  src: string;
  alt: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  priority?: boolean;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({ 
  src, 
  alt, 
  className, 
  width, 
  height,
  priority = false 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement>(null);
  
  useEffect(() => {
    // Reset states when src changes
    setIsLoading(true);
    setHasError(false);
    setImageSrc(src);
    
    // If priority is true, preload the image
    if (priority) {
      const img = new Image();
      img.src = getOptimizedUrl(src);
      img.onload = () => {
        setIsLoading(false);
      };
      img.onerror = () => {
        setIsLoading(false);
        setHasError(true);
      };
    }

    // Check if the image is already cached
    if (imgRef.current && imgRef.current.complete) {
      setIsLoading(false);
    }
  }, [src, priority]);
  
  const handleLoad = () => {
    setIsLoading(false);
  };
  
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };
  
  // Generate a smaller thumbnail version for initial loading
  const getThumbnailUrl = (url: string): string => {
    // For Imgur URLs, we can use their thumbnail service
    if (url.includes('imgur.com')) {
      return url.replace(/\.(jpg|jpeg|png|gif)/, 'm.$1');
    }
    // For Unsplash, we can use their image API
    if (url.includes('unsplash.com')) {
      return url.includes('?') 
        ? `${url}&w=20&q=20&blur=10` 
        : `${url}?w=20&q=20&blur=10`;
    }
    return url;
  };

  // Generate optimized image URL
  const getOptimizedUrl = (url: string): string => {
    // For Imgur URLs, we can use their size parameters
    if (url.includes('imgur.com')) {
      // Use medium quality for better performance
      return url.replace(/\.(jpg|jpeg|png|gif)/, 'l.$1');
    }
    // For Unsplash, we can use their image API
    if (url.includes('unsplash.com')) {
      // Optimize with width and quality parameters
      return url.includes('?') 
        ? `${url}&w=1200&q=80&auto=compress` 
        : `${url}?w=1200&q=80&auto=compress`;
    }
    return url;
  };
  
  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="shimmer absolute inset-0"></div>
        </div>
      )}
      
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <span className="text-gray-400 text-sm">Image not available</span>
        </div>
      )}
      
      {priority ? (
        <img
          ref={imgRef}
          src={getOptimizedUrl(imageSrc)}
          alt={alt}
          width={width}
          height={height}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          style={{ width, height }}
        />
      ) : (
        <>
          {/* Low quality placeholder */}
          {isLoading && (
            <img
              src={getThumbnailUrl(imageSrc)}
              alt={alt}
              className={`${className} blur-sm scale-105 absolute inset-0 w-full h-full object-cover`}
              width={width}
              height={height}
              style={{ width, height }}
              loading="lazy"
            />
          )}
          
          {/* Main image */}
          <img
            ref={imgRef}
            src={getOptimizedUrl(imageSrc)}
            alt={alt}
            className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoad={handleLoad}
            onError={handleError}
            width={width}
            height={height}
            style={{ width, height }}
            loading="lazy"
          />
        </>
      )}
    </div>
  );
};

export default ImageWithLoader;